<template>
    <div class="d-inline-block notranslate">
        <div v-html="splittedCartItemDate"></div>
        <span v-html="priceToDisplay"></span>
        <small v-if="showPricingRules && pricingRules">
            <pricing-rule-text v-for="pricingRule in pricingRules" :key="pricingRule.id" font="font-size: 12px;" :item="item" :spec="spec" :pricing-rule="pricingRule" :as-tooltip="false" :provider="provider" />
        </small>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getExchangedAmount } from '../../bu/CurrencyUtils';
import PricingRuleText from './PricingRuleText.vue';
import { localizedAmount } from '../../currency';

export default {
    props: {
        provider: { type: Object, required: true },
        item: { type: Object, required: false },
        spec: { type: Object, required: false },
        orderItem: { type: Object, required: false },
        cartItem: { type: Object, required: false },
        showPricingRules: { type: Boolean, default: true },
        // 這是給 order item 配合顯示用，因為 order item 不需要換算，直接使用 order.currency
        forceCurrency: { type: String, default: null },
    },
    components: { PricingRuleText },
    computed: {
        ...mapGetters({
            currency: 'currencies/currency',
            currencyRateMap: 'currencies/currencyRateMap',
            currencyOptions: 'currencies/currencyOptions',
        }),
        pricingRules() {
            return this.spec?.pricing_rules
                || this.item?.pricing_rules
                || null;
        },
        priceToDisplay() {
            if (this.cartItem && (this.cartItem.isSplitted || this.cartItem.appliedPricingRule)) {
                return this.formatPriceFromCartItem();
            } else if (this.orderItem) {
                return this.formatPriceFromOrderItem();
            } else {
                return this.formatPrice(this.item.is_renting, this.spec || this.item);
            }
        },
        splittedCartItemDate() {
            if (this.cartItem && this.cartItem.isSplitted) {
                return `<small class="text-muted">${this.cartItem.dateRange.startMoment.format("YYYY-MM-DD")}</small>`;
            }
        }
    },
    methods: {
        localizedAmount,
        formatPriceFromCartItem() {
            return this.formatPrice(this.item.is_renting, this.cartItem);
        },
        formatPriceFromOrderItem() {
            return this.formatPrice(this.orderItem.is_renting, this.orderItem);
        },
        priceRangeTextFromPrices(prices, localizationKey) {
            if (prices.length === 1) {
                return this.$t(localizationKey, { price: prices[0] });
            }
            const sorted = prices.sort((a, b) => a - b);
            const lowest = sorted[0];
            const highest = sorted[sorted.length - 1];
            if (lowest == highest) {
                return this.$t(localizationKey, { price: sorted[0] });
            } else {
                return this.$t(localizationKey, { price: sorted[0] }) + "~" + this.$t(localizationKey, { price: prices[sorted.length - 1] });
            }
        },
        formatPrice(isRenting, buyable) {
            if (isRenting) {
                if (buyable.specs && buyable.specs.length > 0) {
                    let currentType = null;
                    for (var i in buyable.specs) {
                        const spec = buyable.specs[i];
                        if (currentType !== null && spec.renting_charge_type !== currentType) {
                            return this.$t("multiple_charge_type");
                        }
                        currentType = spec.renting_charge_type;
                    }
                    if (!currentType) {
                        return "";
                    }
                    const prices = buyable.specs.map((s) => this.getExchangedAmount(s.renting_charge_amount));
                    return this.priceRangeTextFromPrices(prices, "price_per_" + currentType.toLowerCase());
                } else if (buyable.renting_charge_type) {
                    return this.$t(`price_per_${buyable.renting_charge_type.toLowerCase()}`, {
                        price: this.getExchangedAmount(buyable.renting_charge_amount)
                    });
                } else {
                    return "";
                }
            } else {
                if (buyable.specs && buyable.specs.length > 0) {
                    const sorted = buyable.specs.map((s) => s.price).sort((a, b) => a - b);
                    const lowest = this.getExchangedAmount(sorted[0]);
                    const highest = this.getExchangedAmount(sorted[sorted.length - 1]);
                    let priceString = "";
                    if (lowest == highest) {
                        priceString = lowest;
                    } else {
                        priceString = lowest + "~" + highest;
                    }
                    const sortedOriginalPrice = buyable.specs.map((s) => s.original_price).filter((p) => p).sort((a, b) => a - b);
                    const lowestOriginalPrice = this.getExchangedAmount(sortedOriginalPrice[0]);
                    const highestOriginalPrice = this.getExchangedAmount(sortedOriginalPrice[sortedOriginalPrice.length - 1]);
                    let originalPriceString = "";
                    if (sortedOriginalPrice.length > 0) {
                        if (lowestOriginalPrice == highestOriginalPrice) {
                            originalPriceString = this.formatOriginalPrice(lowestOriginalPrice);
                        } else {
                            const string = lowestOriginalPrice + "~" + highestOriginalPrice;
                            originalPriceString = this.formatOriginalPrice(string);
                        }
                    }
                    return originalPriceString
                        ? originalPriceString + "\n" + priceString
                        : priceString;
                } else {
                    return buyable.original_price
                        ? this.formatOriginalPrice(this.getExchangedAmount(buyable.original_price)) + " " + this.getExchangedAmount(buyable.price)
                        : this.getExchangedAmount(buyable.price);
                }
            }
        },
        formatOriginalPrice(string) {
            return `<span class="original-price">${string}</span>`;
        },
        getExchangedAmount(amount) {
            // if there's force currency means we just use the currency and the amount
            // no need to exchange
            if (this.forceCurrency) {
                return this.localizedAmount(amount, this.forceCurrency, this.currencyOptions);
            }
            return getExchangedAmount({
                rateMap: this.currencyRateMap,
                amount,
                from: this.provider.currency || 'TWD',
                to: this.forceCurrency || this.currency,
                currencyOptions: this.currencyOptions,
            });
        },
    },
}
</script>

<style lang="scss" scoped>
/deep/ .original-price {
    text-decoration: line-through;
    color: #90A4AE;
    font-weight: normal;
}

span {
    word-break: break-word;
}

</style>

<i18n>
{
    "en": {
        "price_per_day": "{price}/day",
        "price_per_booking": "{price}/booking",
        "price_per_night": "{price}/night",
        "price_per_hour": "{price}/hour",
        "fail_to_get_price": "Fail to get price",
        "multiple_charge_type": "Multiple charge types"
    },
    "zh": {
        "price_per_day": "{price}/天",
        "price_per_booking": "{price}/次",
        "price_per_night": "{price}/晚",
        "price_per_hour": "{price}/小時",
        "fail_to_get_price": "無法取得價格",
        "multiple_charge_type": "多種收費方式"
    }
}
</i18n>
